import type { ScanHours, ScanShape } from '@attendance/shared';
import { api } from '../lib/api.js';

/**
 * THE FLAGGED-SCAN QUEUE, PER SCAN — the client half of
 * `packages/api/src/services/flaggedScans.service.ts`, whose header carries the
 * reasoning. In short: the console's older review surface reads
 * `/attendance/live`, which returns each employee's LATEST event, so it can
 * only ever show one flagged punch per person and cannot clear a day.
 */

/** The circle `gpsAt` was tested against, drawn on the evidence map. Null for a polygon-fenced site. */
export interface GpsSiteFence {
  latitude: number;
  longitude: number;
  radiusMeters: number;
}

/**
 * What a punch's GPS says that its filed site does not (server:
 * `punchLocationFlags.ts`). `null` = nothing to say — no fix, no fix time, or
 * the fix agrees with the filed site.
 */
/** A site whose geofence holds the fix, `distanceM` from its centre. */
export interface GpsSite {
  worksiteId: string;
  worksiteName: string;
  /** Absent from an older server. */
  distanceM?: number;
  fence?: GpsSiteFence | null;
}

export interface PunchLocationFlags {
  /** The OTHER worksite whose geofence holds the fix — the nearest when several do. */
  gpsAt: GpsSite | null;
  /** Every further site whose fence ALSO holds it, nearest first (overlapping projects). */
  gpsAlso?: GpsSite[];
  /** Punch time minus fix time, on the device clock. */
  fixAgeSeconds: number | null;
  /** The fix was older than the server's limit at the punch. */
  staleFix: boolean;
  /** The fix came from Wi-Fi or cell radios, not GNSS — so it is not "GPS". */
  approximate: boolean;
}

export interface FlaggedScan extends PunchLocationFlags {
  id: string;
  type: string;
  /** UTC instant. Format it; never truncate it to a day (docs/time-and-dates.md). */
  at: string;
  status: string;
  flaggedReason: string | null;
  source: string;
  worksite: { id: string; name: string } | null;
  faceMatchConfidence: number | null;
  photoUrl: string | null;
  /** Already reaching the day totals provisionally — see the service. */
  countsTowardRollup: boolean;
  /**
   * The id of the earlier scan in this same day that this one repeats, or
   * `null` when it repeats nothing. The server derives it with the INGEST's own
   * rule and the ingest's own window, so the queue and the door agree about
   * what a duplicate is. `null` is an answer, never a gap.
   */
  repeatOf: string | null;
  /**
   * Goes the SAME DIRECTION as the scan before it in this day — two check-ins
   * with no check-out between them. A different fact from `repeatOf`: a repeat
   * is one moment captured twice, this is a day that does not add up. Approving
   * both makes it a real double, which is why the queue is where it is caught.
   */
  sameDirectionAs: string | null;
}

export interface FlaggedScanGroup {
  key: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  /** `YYYY-MM-DD` cut in the COMPANY zone by the server, never by the browser. */
  dayKey: string;
  scans: FlaggedScan[];
}

export interface FlaggedScanPage {
  groups: FlaggedScanGroup[];
  /** How many scans on this page repeat an earlier one in their own day. */
  repeatCount: number;
  /** How many go the same direction as the scan before them in their day. */
  sameDirectionCount: number;
  /** The window they were judged against, in seconds, so the page can say it. */
  repeatWindowSeconds: number;
  hasMore: boolean;
  timezone: string;
}

export interface FlaggedScanDecision {
  decided: number;
  skipped: string[];
}

/** The queue's own key, and the badge's — the badge counts what the page lists. */
export const FLAGGED_SCANS_QUERY_KEY = ['flagged-scans'] as const;
export const FLAGGED_SCAN_COUNT_QUERY_KEY = ['flagged-scans', 'count'] as const;

export const flaggedScansApi = {
  async list(
    params: { worksiteId?: string; limit?: number; q?: string; reasons?: string; shape?: ScanShape; hours?: ScanHours } = {}
  ): Promise<FlaggedScanPage> {
    const body = await api.get<{ data: FlaggedScanPage }>('/attendance/events/flagged', {
      params,
    });
    return body.data;
  },

  async count(): Promise<number> {
    const body = await api.get<{ data: { count: number } }>('/attendance/events/flagged/count');
    return body.data.count;
  },

  /**
   * One press, one act. The server writes every status before it rebuilds any
   * day, so a day is never derived from a half-decided punch stream — which is
   * why the page sends a set rather than looping the single-event endpoint.
   */
  async decide(payload: {
    eventIds: string[];
    status: 'VALID' | 'REJECTED';
    reason: string;
  }): Promise<FlaggedScanDecision> {
    const body = await api.patch<{ data: FlaggedScanDecision }>(
      '/attendance/events/status',
      payload
    );
    return body.data;
  },

  /**
   * "File at GPS site": move one scan to the site its own fresh fix is inside
   * — one of the `gpsAt` / `gpsAlso` this row already prints (server:
   * flaggedScanRefile.ts), named by `worksiteId`, which the server requires
   * when the fix lies inside several fences. The status is untouched; the scan
   * is still decided in the queue.
   */
  async fileAtGpsSite(eventId: string, worksiteId: string): Promise<{ id: string; worksiteId: string; from: string | null; to: string }> {
    const body = await api.post<{ data: { id: string; worksiteId: string; from: string | null; to: string } }>(
      `/attendance/events/${encodeURIComponent(eventId)}/file-at-gps-site`,
      { worksiteId }
    );
    return body.data;
  },
};
